import { useEffect, useRef, useCallback, useReducer } from 'preact/hooks';
import { camelizeKeys } from 'humps';
import { buildAvailabilityApiUrl, indexByDate } from '../utils/availabilityServiceHelpers';
import Polling from '../../../utils/Polling';

/**
 * @typedef {Object} AvailabilityState
 * @property {Record<string, Object>} departure  Days indexed by `YYYY-MM-DD` for the outbound leg.
 * @property {Record<string, Object>} return     Days indexed by `YYYY-MM-DD` for the return leg.
 * @property {Object|null} meta                  Camelized `meta` from the latest API response.
 * @property {boolean} fetching                  True while a polling cycle is in flight.
 * @property {{ dep: boolean, ret: boolean }} fetched
 *   True per leg once the corresponding poll has reached a terminal state at least once.
 */
const initialState = {
  departure: {},
  return: {},
  meta: null,
  fetching: false,
  fetched: { dep: false, ret: false },
};

const fetchedKeyForLeg = {
  departure: 'dep',
  return: 'ret',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'POLL_START': {
      const { leg } = action.payload;
      const fetchedKey = fetchedKeyForLeg[leg];
      return {
        ...state,
        fetching: true,
        fetched: { ...state.fetched, [fetchedKey]: false },
      };
    }
    case 'POLL_RESET_LEG': {
      const { leg } = action.payload;
      const fetchedKey = fetchedKeyForLeg[leg];
      return {
        ...state,
        [leg]: {},
        meta: null,
        fetched: { ...state.fetched, [fetchedKey]: false },
      };
    }
    case 'POLL_RESET_SCOPE':
      return {
        ...state,
        departure: {},
        return: {},
        meta: null,
        fetched: { dep: false, ret: false },
      };
    case 'POLL_UPDATE': {
      const { leg, indexed, meta } = action.payload;
      return {
        ...state,
        [leg]: { ...state[leg], ...indexed },
        meta: meta ?? state.meta,
        fetching: true,
      };
    }
    case 'POLL_DONE': {
      const { leg, indexed, meta } = action.payload;
      const fetchedKey = fetchedKeyForLeg[leg];
      return {
        ...state,
        [leg]: { ...state[leg], ...indexed },
        meta: meta ?? state.meta,
        fetching: false,
        fetched: { ...state.fetched, [fetchedKey]: true },
      };
    }
    case 'POLL_DEGRADED': {
      const { leg } = action.payload;
      const fetchedKey = fetchedKeyForLeg[leg];
      return {
        ...state,
        fetching: false,
        fetched: { ...state.fetched, [fetchedKey]: true },
      };
    }
    default:
      return state;
  }
};

const MAX_ATTEMPTS = 6;
const DEFAULT_POLL_DELAY_MS = 5000;
const MIN_POLL_DELAY_MS = 2000;
const MAX_POLL_DELAY_MS = 30000;
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Polls the `/search/calendar` availability endpoint for a route + date window
 * and exposes the per-leg results in a stable, indexed-by-date shape.
 *
 * The hook is leg-aware: it caches `departure` and `return` independently so
 * switching legs (e.g. when the user picks an outbound date) does not refetch
 * already-fetched data. A scope change (origin/destination) clears the
 * affected leg before the next poll starts.
 *
 * Polling uses the `Polling` utility with a per-attempt timeout
 * ({@link REQUEST_TIMEOUT_MS}) and a maximum number of attempts
 * ({@link MAX_ATTEMPTS}). On a degraded run (network error, timeout, or
 * `completion_state !== 'complete'` after the last attempt) the hook stops
 * polling and surfaces whatever partial results it received via
 * `state[leg]`.
 *
 * @param {Object}   params
 * @param {string}   params.origin            Origin slug (e.g. "cdmx"). Polling is skipped when falsy.
 * @param {string}   params.destination       Destination slug. Polling is skipped when falsy.
 * @param {string}   params.from              ISO date (YYYY-MM-DD) - inclusive start of the window.
 * @param {string}   params.to                ISO date (YYYY-MM-DD) - inclusive end of the window.
 * @param {'departure'|'return'} [params.leg='departure']  Which leg to fetch; controls the `round=true` query param.
 * @param {boolean}  [params.enabled=true]    When false, the hook is inert (no fetches, no resets).
 * @param {Object}   [params.options={}]
 * @param {string}   params.options.sourceUrl Base URL of the search API; required to fetch.
 * @param {boolean}  [params.options.forceHttps=false] Force the API URL to https.
 *
 * @returns {{
 *   departure: Record<string, Object>,
 *   return: Record<string, Object>,
 *   meta: Object|null,
 *   fetching: boolean,
 *   fetched: { dep: boolean, ret: boolean },
 * }} `departure`/`return` are indexed by `YYYY-MM-DD`. `fetching` is true between
 *    `start()` and the next terminal event (done or degraded). `fetched[leg]`
 *    flips true the first time each leg reaches a terminal state.
 */
const useCalendarAvailability = ({
  origin,
  destination,
  from,
  to,
  leg = 'departure',
  enabled = true,
  options = {},
}) => {
  const { sourceUrl, forceHttps = false } = options;

  const [state, dispatch] = useReducer(reducer, initialState);
  const cacheRef = useRef(new Map());
  const pollersRef = useRef({ departure: null, return: null });
  const scopeRef = useRef({ departure: null, return: null });

  const fetchAvailability = useCallback(
    async ({ origin: o, destination: d, from: f, to: t, round, signal }) => {
      const url = buildAvailabilityApiUrl({
        origin: o,
        destination: d,
        from: f,
        to: t,
        sourceUrl,
        forceHttps,
        round,
      });
      if (!url) return null;
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    [sourceUrl, forceHttps]
  );

  useEffect(() => {
    if (!enabled || !origin || !destination || !from || !to || !sourceUrl) return undefined;

    const scopeKey = `${origin}|${destination}`;
    if (scopeRef.current[leg] !== scopeKey) {
      scopeRef.current = { departure: scopeKey, return: scopeKey };
      dispatch({ type: 'POLL_RESET_SCOPE' });
    }

    const key = `${origin}|${destination}|${from}|${to}|${leg}`;

    if (cacheRef.current.has(key)) {
      dispatch({ type: 'POLL_DONE', payload: cacheRef.current.get(key) });
      return undefined;
    }

    const pollers = pollersRef.current;
    pollers[leg]?.stop();

    const round = leg === 'return';

    const buildPayload = (raw) => ({
      leg,
      indexed: indexByDate(raw?.[leg]),
      meta: raw?.meta ? camelizeKeys(raw.meta) : null,
    });

    const poller = new Polling({
      fetchFn: (signal) =>
        fetchAvailability({ origin, destination, from, to, round, signal }),
      isDone: (raw) => raw?.meta?.completion_state === 'complete',
      getDelay: (raw) => raw?.meta?.warmup?.poll_after_ms,
      maxAttempts: MAX_ATTEMPTS,
      defaultDelay: DEFAULT_POLL_DELAY_MS,
      minDelay: MIN_POLL_DELAY_MS,
      maxDelay: MAX_POLL_DELAY_MS,
      requestTimeoutMs: REQUEST_TIMEOUT_MS,
      onUpdate: (raw) => {
        dispatch({ type: 'POLL_UPDATE', payload: buildPayload(raw) });
      },
      onDone: (raw) => {
        const payload = buildPayload(raw);
        cacheRef.current.set(key, payload);
        dispatch({ type: 'POLL_DONE', payload });
      },
      onDegraded: (reason) => {
        if (reason === 'fetch-error' || reason === 'request-timeout') {
          console.error('[useCalendarAvailability] poll degraded:', reason);
        }
        dispatch({ type: 'POLL_DEGRADED', payload: { leg } });
      },
    });

    pollers[leg] = poller;
    dispatch({ type: 'POLL_START', payload: { leg } });
    poller.start();

    return () => {
      poller.stop();
      if (pollers[leg] === poller) {
        pollers[leg] = null;
      }
    };
  }, [origin, destination, from, to, leg, enabled, sourceUrl, forceHttps, fetchAvailability]);

  return {
    departure: state.departure,
    return: state.return,
    meta: state.meta,
    fetching: state.fetching,
    fetched: state.fetched,
  };
};

export default useCalendarAvailability;
